<template>
    <div class="content-wrapper">
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <button type="button" class="btn btn-label-secondary mb-3" @click.prevent="goBack()">
                        <i class="ti ti-arrow-right me-1"></i> رجوع للمنتجات
                    </button>
                    <h4 class="mb-1">
                        {{ product.productName || product.name || 'تفاصيل المنتج' }}
                        <span class="badge ms-2" :class="statusBadgeClass">{{ statusLabel }}</span>
                    </h4>
                    <p class="text-muted mb-0">
                        {{ product.nationalFormularyName || 'الصنف النمطي غير محدد' }}
                        <span v-if="product.nationalCode"> — <code>{{ product.nationalCode }}</code></span>
                    </p>
                </div>
                <div class="d-flex flex-wrap gap-2">
                    <button type="button" class="btn btn-primary" @click.prevent="openBasicEdit()">
                        <i class="ti ti-edit me-1"></i> تعديل
                    </button>
                    <button type="button" class="btn btn-label-warning" @click.prevent="toggleStatus()">
                        <i class="ti me-1" :class="Number(product.status) === 1 ? 'ti-eye-off' : 'ti-eye-check'"></i>
                        {{ Number(product.status) === 1 ? 'تعطيل' : 'تفعيل' }}
                    </button>
                    <button type="button" class="btn btn-label-danger" @click.prevent="deleteProduct()">
                        <i class="ti ti-trash me-1"></i> حذف
                    </button>
                </div>
            </div>

            <div class="nav-align-top">
                <ul class="nav nav-tabs mb-3" role="tablist">
                    <li class="nav-item">
                        <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#basic-tab" type="button" role="tab">
                            <i class="ti ti-info-circle me-1"></i> المعلومات الأساسية
                        </button>
                    </li>
                    <li class="nav-item">
                        <button class="nav-link" data-bs-toggle="tab" data-bs-target="#packagings-tab" type="button" role="tab">
                            <i class="ti ti-package me-1"></i> العبوات
                        </button>
                    </li>
                    <li class="nav-item">
                        <button class="nav-link" data-bs-toggle="tab" data-bs-target="#stock-tab" type="button" role="tab">
                            <i class="ti ti-building-warehouse me-1"></i> المخزون
                        </button>
                    </li>
                    <li class="nav-item">
                        <button class="nav-link" data-bs-toggle="tab" data-bs-target="#statistics-tab" type="button" role="tab">
                            <i class="ti ti-chart-line me-1"></i> الإحصائيات
                        </button>
                    </li>
                </ul>

                <div class="tab-content p-0 bg-transparent shadow-none">
                    <div class="tab-pane fade show active" id="basic-tab" role="tabpanel">
                        <BasicInfoTab :product="product" @edit="openBasicEdit" />
                    </div>
                    <div class="tab-pane fade" id="packagings-tab" role="tabpanel">
                        <PackagingsTab :product-id="productId" :initial-packagings="packagings" />
                    </div>
                    <div class="tab-pane fade" id="stock-tab" role="tabpanel">
                        <WarehouseStockTab :stock-items="warehouseStock" />
                    </div>
                    <div class="tab-pane fade" id="statistics-tab" role="tabpanel">
                        <StatisticsTab :statistics="statistics" />
                    </div>
                </div>
            </div>

            <ProductFormModal ref="productFormModal"
                              :manufacturers="manufacturers"
                              :countries="countries"
                              :formulary-items="formularyItems"
                              @saved="loadDetails" />
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import BasicInfoTab from './ProductDetailsTabs/BasicInfoTab.vue';
import PackagingsTab from './ProductDetailsTabs/PackagingsTab.vue';
import StatisticsTab from './ProductDetailsTabs/StatisticsTab.vue';
import WarehouseStockTab from './ProductDetailsTabs/WarehouseStockTab.vue';
import ProductFormModal from './ProductFormModal.vue';

export default {
    name: 'ProductDetails',
    mixins: [HelperMixin],
    components: {
        BasicInfoTab,
        PackagingsTab,
        StatisticsTab,
        WarehouseStockTab,
        ProductFormModal
    },
    data() {
        return {
            productId: Number(this.$route.params.id),
            product: {},
            packagings: [],
            statistics: {},
            warehouseStock: [],
            manufacturers: [],
            countries: [],
            formularyItems: []
        };
    },
    computed: {
        statusLabel() {
            return Number(this.product.status) === 1 ? 'نشط' : 'غير نشط';
        },
        statusBadgeClass() {
            return Number(this.product.status) === 1 ? 'bg-label-success' : 'bg-label-warning';
        }
    },
    async created() {
        await this.CheckLoginStatus();
        await Promise.all([
            this.loadLookups(),
            this.loadDetails()
        ]);
    },
    methods: {
        goBack() {
            this.$router.push({ path: '/Products' });
        },
        async loadLookups() {
            try {
                const [manufacturers, countries, formulary] = await Promise.all([
                    this.$http.GetAllManufacturers(),
                    this.$http.GetAllCountries(),
                    this.$http.GetFormulary(1, 500, '')
                ]);
                this.manufacturers = manufacturers.data.info || [];
                this.countries = countries.data.info || [];
                this.formularyItems = formulary.data.info || [];
            } catch {
                this.manufacturers = [];
                this.countries = [];
                this.formularyItems = [];
            }
        },
        async loadDetails() {
            if (!this.productId) return;
            this.$blockUI.Start();
            try {
                const response = await this.$http.GetProductDetails(this.productId);
                this.product = response.data.info || {};
                this.packagings = response.data.packagings || [];
                this.statistics = response.data.statistics || {};
                this.warehouseStock = response.data.warehouseStock || [];
            } catch (err) {
                const message = err && err.response && err.response.data ? err.response.data : 'تعذر تحميل بيانات المنتج';
                this.$helper.ShowMessage('error', 'خطأ', message);
            } finally {
                this.$blockUI.Stop();
            }
        },
        openBasicEdit() {
            this.$refs.productFormModal.open(this.product);
        },
        toggleStatus() {
            if (!this.productId) return;
            Swal.fire({
                title: 'تأكيد العملية',
                text: 'هل أنت متأكد من تغيير حالة المنتج؟',
                icon: 'question',
                customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-label-secondary ms-2' },
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'تأكيد',
                cancelButtonText: 'إلغاء'
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.ChangeStatusProducts(this.productId)
                    .then(response => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                        this.loadDetails();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const message = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', 'خطأ بالعملية', message);
                    });
            });
        },
        deleteProduct() {
            if (!this.productId) return;
            Swal.fire({
                title: 'تأكيد الحذف',
                text: 'هل أنت متأكد من حذف هذا المنتج؟ لا يمكن التراجع عن هذه العملية.',
                icon: 'warning',
                customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-label-secondary ms-2' },
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'حذف',
                cancelButtonText: 'إلغاء'
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DeleteProducts(this.productId)
                    .then(response => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                        this.$router.push({ path: '/Products' });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        const message = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
                        this.$helper.ShowMessage('error', 'خطأ بعملية الحذف', message);
                    });
            });
        }
    }
};
</script>
